import {
  GLASS_REACH_M,
  LOOT_REACH_M,
  type AABB,
  type ContainerKind,
  type GlassAct,
  type MapDef,
  type Vec3,
} from "@rietz/shared";
import type { Input } from "../input/input.js";
import type { InterpPose } from "../net/interpolation.js";
import { setInteractPrompt } from "../ui/store.js";
import type { DroppedItems } from "./droppedItems.js";
import type { InventoryController } from "./inventory.js";

/**
 * The interact key: what is in front of you, and what happens when you press
 * it. Containers have real geometry, so they get a RAY test from the eye — "the
 * one you are looking at" is the only answer a player accepts in a pantry.
 * Corpses are a sprawl with no box, so they get a generous 1 m cylinder around
 * the view ray; dropped items on the floor get a tighter one. All three are
 * gated on `LOOT_REACH_M` from the FEET, the server's own measurement: generous
 * with the aim, strict with the distance, so no prompt is offered for something
 * the server would refuse. Window panes are the fourth kind, on `GLASS_REACH_M`
 * and a ray against a GROWN box — a pane is 6 cm thick and unaimable otherwise;
 * intact it is smashed, broken it is climbed through. Nearest wins.
 * Allocation-free — every rendered frame.
 */

/** Aim tolerance for a corpse, metres of perpendicular distance from the ray. */
const CORPSE_AIM_TOLERANCE_M = 1;
/** ...and for a dropped item, which is a much smaller thing to point at but is
 *  usually so close that the tolerance is a cone of many degrees anyway. */
const ITEM_AIM_TOLERANCE_M = 0.5;
/** Corpses lie flat; aim at the middle of the sprawl rather than the ground. */
const CORPSE_AIM_HEIGHT_M = 0.3;

/** A pane's box is grown by this before the ray test: 15 cm on its two wide
 *  axes, and 30 cm on the thin one so it can be hit from an angle. */
const GLASS_AIM_GROW_M = 0.15;
const GLASS_AIM_GROW_THIN_M = 0.3;

/** English prompt per container kind. The key label ("F") is the HUD's. */
const CONTAINER_PROMPTS: Record<ContainerKind, string> = {
  crate: "Open crate",
  cupboard: "Search cupboard",
  shelf: "Search shelf",
  toolbox: "Open toolbox",
  fridge: "Open fridge",
  chest: "Open chest",
};

type TargetKind =
  | "none"
  | "container"
  | "corpse"
  | "item"
  | "glassIntact"
  | "glassBroken";

export interface InteractOptions {
  input: Input;
  inventory: InventoryController;
  droppedItems: DroppedItems;
  /**
   * Getter: the map is replaced wholesale on `welcome`, and `null` until the
   * first one is installed.
   */
  map: () => MapDef | null;
  /** The poses currently being DRAWN — corpses are among them. */
  poses: () => readonly InterpPose[];
  /** Has pane `id` already been broken? Drives the prompt and the act. */
  glassBroken: (id: number) => boolean;
  /** Ask the server to smash or climb. A no-op offline. */
  glass: (id: number, act: GlassAct) => void;
}

export class Interact {
  private readonly o: InteractOptions;

  /** Best candidate this frame. Fields rather than an object: no allocation. */
  private targetKind: TargetKind = "none";
  private targetId = "";
  private targetLabel: string | null = null;
  private targetDist = 0;

  /** Scratch — the eye ray, rebuilt every frame. */
  private readonly eye: Vec3 = { x: 0, y: 0, z: 0 };
  private readonly dir: Vec3 = { x: 0, y: 0, z: 0 };

  /** Scratch — one pane's grown aim box, rewritten per candidate. */
  private readonly aimBox: AABB = {
    min: { x: 0, y: 0, z: 0 },
    max: { x: 0, y: 0, z: 0 },
  };

  constructor(opts: InteractOptions) {
    this.o = opts;
  }

  /**
   * One frame: pick a target, publish the prompt, act on a press.
   * `feet`/`eyeHeight` are the RENDERED pair.
   */
  update(feet: Readonly<Vec3>, eyeHeight: number): void {
    // The inventory screen is up: no crosshair, no world interaction, and any
    // press is stale intent — drained.
    if (this.o.inventory.isScreenOpen) {
      this.o.input.consumeInteractPresses();
      this.clearTarget();
      return;
    }

    // No world yet — the press is drained for the same reason.
    const map = this.o.map();
    if (map === null) {
      this.o.input.consumeInteractPresses();
      this.clearTarget();
      return;
    }

    this.eye.x = feet.x;
    this.eye.y = feet.y + eyeHeight;
    this.eye.z = feet.z;
    viewDirection(this.o.input.yaw, this.o.input.pitch, this.dir);

    this.targetKind = "none";
    this.targetLabel = null;
    this.targetDist = Infinity;

    this.scanContainers(feet, map);
    this.scanCorpses(feet);
    this.scanItems(feet);
    this.scanGlass(feet, map);

    setInteractPrompt(this.targetLabel);

    if (this.o.input.consumeInteractPresses() > 0) this.act();
  }

  /** Hides the prompt — disconnect, death, map change. */
  clearTarget(): void {
    if (this.targetKind === "none" && this.targetLabel === null) return;
    this.targetKind = "none";
    this.targetId = "";
    this.targetLabel = null;
    setInteractPrompt(null);
  }

  // -----------------------------------------------------------------------

  private act(): void {
    switch (this.targetKind) {
      case "container":
        this.o.inventory.openContainer(this.targetId);
        return;
      case "corpse":
        // Corpse containers are named after the player who left them; the
        // server mints no separate id for a body.
        this.o.inventory.openContainer(`corpse:${this.targetId}`);
        return;
      case "item":
        this.o.inventory.pickup(this.targetId);
        return;
      case "glassIntact":
        this.o.glass(Number(this.targetId), "smash");
        return;
      case "glassBroken":
        this.o.glass(Number(this.targetId), "climb");
        return;
      case "none":
        return;
    }
  }

  private consider(kind: TargetKind, id: string, label: string, dist: number): void {
    if (dist >= this.targetDist) return;
    this.targetKind = kind;
    this.targetId = id;
    this.targetLabel = label;
    this.targetDist = dist;
  }

  /**
   * Map containers: a linear scan over `lootSpawns`, a few thousand flops a
   * frame — cheaper than a spatial index rebuilt on every map change.
   */
  private scanContainers(feet: Readonly<Vec3>, map: MapDef): void {
    const spawns = map.lootSpawns;
    if (spawns === undefined) return;

    for (const spawn of spawns) {
      const dist = distanceToBox(feet, spawn.box);
      if (dist > LOOT_REACH_M) continue;
      // The reach test is the cheap one and rejects almost everything, so the
      // ray only ever runs for the handful of boxes at arm's length.
      if (!rayHitsBox(this.eye, this.dir, spawn.box)) continue;
      this.consider("container", spawn.id, CONTAINER_PROMPTS[spawn.kind], dist);
    }
  }

  private scanCorpses(feet: Readonly<Vec3>): void {
    for (const pose of this.o.poses()) {
      if (pose.anim !== "dead") continue;
      const dist = distance(feet, pose.pos);
      if (dist > LOOT_REACH_M) continue;
      const perp = perpDistance(
        this.eye,
        this.dir,
        pose.pos.x,
        pose.pos.y + CORPSE_AIM_HEIGHT_M,
        pose.pos.z,
      );
      if (perp > CORPSE_AIM_TOLERANCE_M) continue;
      this.consider("corpse", pose.id, "Search body", dist);
    }
  }

  private scanItems(feet: Readonly<Vec3>): void {
    for (const item of this.o.droppedItems.items) {
      const dist = distance(feet, item.pos);
      if (dist > LOOT_REACH_M) continue;
      const perp = perpDistance(this.eye, this.dir, item.pos.x, item.pos.y, item.pos.z);
      if (perp > ITEM_AIM_TOLERANCE_M) continue;
      // The prompt tells the truth about the outcome; the key still asks, and
      // the server decides.
      const label = this.o.inventory.canHold(item.defId)
        ? `Pick up: ${item.name}`
        : `No room: ${item.name}`;
      this.consider("item", item.id, label, dist);
    }
  }

  /**
   * Window panes. The pane's id is its INDEX in `map.glass`, so the target id
   * is that index as a string — `act` turns it back into a number.
   */
  private scanGlass(feet: Readonly<Vec3>, map: MapDef): void {
    const panes = map.glass;
    if (panes === undefined) return;

    for (let i = 0; i < panes.length; i++) {
      const pane = panes[i];
      if (pane === undefined) continue;
      const dist = distanceToBox(feet, pane.box);
      if (dist > GLASS_REACH_M) continue;
      if (!rayHitsBox(this.eye, this.dir, this.growPaneBox(pane.box))) continue;
      const broken = this.o.glassBroken(i);
      this.consider(
        broken ? "glassBroken" : "glassIntact",
        String(i),
        broken ? "Climb through" : "Smash window",
        dist,
      );
    }
  }

  /** Writes `box` grown for the aim test into the shared scratch. No allocation. */
  private growPaneBox(box: AABB): AABB {
    const thinX = box.max.x - box.min.x <= box.max.z - box.min.z;
    const gx = thinX ? GLASS_AIM_GROW_THIN_M : GLASS_AIM_GROW_M;
    const gz = thinX ? GLASS_AIM_GROW_M : GLASS_AIM_GROW_THIN_M;
    const out = this.aimBox;
    out.min.x = box.min.x - gx;
    out.min.y = box.min.y - GLASS_AIM_GROW_M;
    out.min.z = box.min.z - gz;
    out.max.x = box.max.x + gx;
    out.max.y = box.max.y + GLASS_AIM_GROW_M;
    out.max.z = box.max.z + gz;
    return out;
  }
}

// ---------------------------------------------------------------------------
// Geometry. All of it writes into caller-owned scratch or returns a number.
// ---------------------------------------------------------------------------

/**
 * Unit view direction for `yaw`/`pitch` — yaw 0 down −Z, positive pitch up, NO
 * zero elevation and no spread: where the crosshair points. Exported for the
 * hideout's station scan, which shares the three primitives.
 */
export function viewDirection(yaw: number, pitch: number, out: Vec3): void {
  const cp = Math.cos(pitch);
  out.x = -Math.sin(yaw) * cp;
  out.y = Math.sin(pitch);
  out.z = -Math.cos(yaw) * cp;
}

function distance(a: Readonly<Vec3>, b: Readonly<Vec3>): number {
  return Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z);
}

/** Distance from a point to the nearest point of an AABB. 0 when inside.
 *  Exported for the hideout's station scan — see `viewDirection`. */
export function distanceToBox(p: Readonly<Vec3>, box: AABB): number {
  const dx = p.x < box.min.x ? box.min.x - p.x : p.x > box.max.x ? p.x - box.max.x : 0;
  const dy = p.y < box.min.y ? box.min.y - p.y : p.y > box.max.y ? p.y - box.max.y : 0;
  const dz = p.z < box.min.z ? box.min.z - p.z : p.z > box.max.z ? p.z - box.max.z : 0;
  return Math.hypot(dx, dy, dz);
}

/**
 * Perpendicular distance from the ray to a point — how far off the crosshair it
 * is. A point BEHIND the eye is measured from the eye (`t` clamped at 0), so it
 * fails any tolerance.
 */
function perpDistance(
  eye: Readonly<Vec3>,
  dir: Readonly<Vec3>,
  x: number,
  y: number,
  z: number,
): number {
  const vx = x - eye.x;
  const vy = y - eye.y;
  const vz = z - eye.z;
  let t = vx * dir.x + vy * dir.y + vz * dir.z;
  if (t < 0) t = 0;
  const px = vx - dir.x * t;
  const py = vy - dir.y * t;
  const pz = vz - dir.z * t;
  return Math.hypot(px, py, pz);
}

/**
 * Slab test: does the ray from `eye` along `dir` enter `box`? No `t` —
 * candidates rank by DISTANCE FROM THE FEET. An eye inside the box counts as a
 * hit; a zero direction component is handled by the origin-inside-slab test.
 * Exported for the hideout's station scan.
 */
export function rayHitsBox(eye: Readonly<Vec3>, dir: Readonly<Vec3>, box: AABB): boolean {
  let tMin = 0;
  let tMax = Infinity;

  for (let axis = 0; axis < 3; axis++) {
    const o = axis === 0 ? eye.x : axis === 1 ? eye.y : eye.z;
    const d = axis === 0 ? dir.x : axis === 1 ? dir.y : dir.z;
    const lo = axis === 0 ? box.min.x : axis === 1 ? box.min.y : box.min.z;
    const hi = axis === 0 ? box.max.x : axis === 1 ? box.max.y : box.max.z;

    if (Math.abs(d) < 1e-9) {
      // Parallel to this pair of planes: either we are between them forever, or
      // we never cross them.
      if (o < lo || o > hi) return false;
      continue;
    }
    const inv = 1 / d;
    let t0 = (lo - o) * inv;
    let t1 = (hi - o) * inv;
    if (t0 > t1) {
      const swap = t0;
      t0 = t1;
      t1 = swap;
    }
    if (t0 > tMin) tMin = t0;
    if (t1 < tMax) tMax = t1;
    if (tMin > tMax) return false;
  }
  return true;
}
